import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtEUR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Plus, Minus, PackageCheck } from "lucide-react";

const BUNDLES = [
  { size: 6, discount: 5, code: "BUNDLE6", label: "Six-bar box" },
  { size: 12, discount: 10, code: "BUNDLE12", label: "Twelve-bar crate" },
];

export default function Bundle() {
  const [products, setProducts] = useState([]);
  const [size, setSize] = useState(6);
  const [pick, setPick] = useState({}); // product_id -> qty
  const [applying, setApplying] = useState(false);
  const { add, setOpen } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/products", { params: { category: "protein-bars" } }).then((r) => setProducts(r.data));
  }, []);

  const currentBundle = BUNDLES.find((b) => b.size === size);
  const totalPicked = Object.values(pick).reduce((s, n) => s + n, 0);
  const subtotal = useMemo(() => products.reduce((s, p) => s + (pick[p.id] || 0) * p.price_eur, 0), [pick, products]);
  const saving = subtotal * (currentBundle.discount / 100);
  const priced = subtotal - saving;

  const inc = (p) => {
    if (totalPicked >= size) return;
    setPick({ ...pick, [p.id]: (pick[p.id] || 0) + 1 });
  };
  const dec = (p) => {
    const c = (pick[p.id] || 0);
    if (c <= 0) return;
    const n = { ...pick, [p.id]: c - 1 };
    if (n[p.id] === 0) delete n[p.id];
    setPick(n);
  };

  const addBundle = async () => {
    if (totalPicked !== size) { toast.error(`Pick exactly ${size} bars`); return; }
    setApplying(true);
    // Add each pick to cart
    for (const p of products) {
      const qty = pick[p.id] || 0;
      if (qty > 0) add(p, qty);
    }
    // Store bundle code for auto-apply at checkout
    localStorage.setItem("brunchi_bundle_code", currentBundle.code);
    toast.success(`${currentBundle.label} added · ${currentBundle.discount}% off will apply`);
    setApplying(false);
    setOpen(false);
    nav("/checkout");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16" data-testid="bundle-page">
      <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brunchi-green-hover)] mb-3">Build your own</div>
      <h1 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.05]">
        Mix your Brunchi box.
      </h1>
      <p className="mt-3 text-[color:var(--brunchi-ink-soft)] max-w-xl">
        Pick any {BUNDLES.map((b) => b.size).join(" or ")} protein bars and save up to 10%. One flat price, one satisfied pantry.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        {BUNDLES.map((b) => (
          <button key={b.size} onClick={() => { setSize(b.size); setPick({}); }}
            data-testid={`bundle-size-${b.size}`}
            className={`px-6 py-3 rounded-full text-sm font-medium border-2 transition ${
              size === b.size
                ? "bg-[color:var(--brunchi-green)] border-[color:var(--brunchi-green)] text-white"
                : "bg-white border-[color:var(--brunchi-stone-2)] text-[color:var(--brunchi-ink)]"
            }`}>
            {b.label} · save {b.discount}%
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-10">
        <div className="lg:col-span-2">
          <div className="text-sm text-[color:var(--brunchi-ink-soft)] mb-3" data-testid="bundle-progress">
            <span className="font-mono font-semibold">{totalPicked}</span> / {size} bars picked
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {products.length === 0 && (
              <div className="col-span-2 py-12 text-center text-[color:var(--brunchi-ink-soft)]">Loading bars…</div>
            )}
            {products.map((p) => {
              const qty = pick[p.id] || 0;
              return (
                <div key={p.id} className="flex gap-3 bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-3" data-testid={`bundle-product-${p.slug}`}>
                  {p.images?.[0] && <img src={p.images[0]} alt="" className="w-20 h-20 object-cover rounded-lg" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-medium text-sm truncate">{p.name}</div>
                    <div className="text-xs text-[color:var(--brunchi-ink-soft)]">{fmtEUR(p.price_eur)}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => dec(p)} data-testid={`bundle-dec-${p.slug}`}
                        className="w-7 h-7 rounded-full border border-[color:var(--brunchi-stone-2)] flex items-center justify-center"><Minus size={12} /></button>
                      <span className="font-mono text-sm w-5 text-center" data-testid={`bundle-qty-${p.slug}`}>{qty}</span>
                      <button onClick={() => inc(p)} disabled={totalPicked >= size} data-testid={`bundle-inc-${p.slug}`}
                        className="w-7 h-7 rounded-full border border-[color:var(--brunchi-stone-2)] flex items-center justify-center disabled:opacity-30"><Plus size={12} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <aside className="bg-[color:var(--brunchi-cream)] rounded-2xl p-6 h-fit sticky top-24 border border-[color:var(--brunchi-stone)]">
          <div className="text-xs uppercase tracking-widest text-[color:var(--brunchi-green-hover)]">Your box</div>
          <div className="font-display text-2xl font-semibold mt-1">{currentBundle.label}</div>
          <div className="mt-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><span>Bars</span><span className="font-mono">{totalPicked} / {size}</span></div>
            <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{fmtEUR(subtotal)}</span></div>
            <div className="flex justify-between text-[color:var(--brunchi-success)]"><span>Bundle saving</span><span className="font-mono">−{fmtEUR(saving)}</span></div>
            <div className="flex justify-between font-semibold text-base pt-2 border-t border-[color:var(--brunchi-stone)]">
              <span>Bundle price</span><span className="font-mono text-[color:var(--brunchi-green-hover)]" data-testid="bundle-price">{fmtEUR(priced)}</span>
            </div>
          </div>
          <button onClick={addBundle} disabled={totalPicked !== size || applying}
            data-testid="bundle-add-btn"
            className="mt-6 w-full py-3 rounded-full pill-primary font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2">
            <PackageCheck size={16} /> Add box to basket
          </button>
        </aside>
      </div>
    </div>
  );
}
